import { a as __rest } from '../../tslib.es6.esm.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useId, useState, useRef, useEffect } from 'react';
import * as RadixPopover from '@radix-ui/react-popover';
import tooltipStyles from './Tooltip.styles.esm.js';
import { cn } from '../../utils/cn.esm.js';

/**
 * Tooltip displays additional information when users hover over, focus on, or tap an element.
 * Use tooltips to provide helpful information or context about UI elements.
 */
const Tooltip = (_a) => {
    var { trigger, children, className, inverse = false, side = "top", align = "center", delayDuration = 100, defaultOpen, sideOffset, onOpenChange } = _a, props = __rest(_a, ["trigger", "children", "className", "inverse", "side", "align", "delayDuration", "defaultOpen", "sideOffset", "onOpenChange"]);
    // Get the styles from tailwind-variants
    const { content: contentStyles, arrow: arrowStyles } = tooltipStyles({
        inverse,
    });
    const generatedId = useId();
    const [isOpen, setIsOpen] = useState(defaultOpen !== null && defaultOpen !== void 0 ? defaultOpen : false);
    const triggerRef = useRef(null);
    const contentRef = useRef(null);
    const isTouchDevice = () => {
        return "ontouchstart" in window || navigator.maxTouchPoints > 0;
    };
    useEffect(() => {
        if (!isOpen && (triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current)) {
            // avoid auto focus due to onMouseLeave event
            triggerRef.current.focus();
            triggerRef.current.blur();
        }
    }, [isOpen]);
    const handleDisplayTooltip = (value) => {
        setTimeout(() => {
            setIsOpen(value);
        }, delayDuration);
    };
    const handleKeyDownTrigger = (e) => {
        if (e.code === "Enter" || e.code === "Space") {
            handleDisplayTooltip(!isOpen);
        }
    };
    return (jsxs(RadixPopover.Root, { open: isOpen, onOpenChange: setIsOpen, children: [jsx(RadixPopover.Trigger, { asChild: true, ref: triggerRef, "aria-describedby": props["aria-describedby"] || generatedId, onKeyDown: (e) => handleKeyDownTrigger(e), onMouseEnter: () => {
                    if (!isTouchDevice())
                        handleDisplayTooltip(true);
                }, onMouseLeave: () => {
                    if (!isTouchDevice())
                        handleDisplayTooltip(false);
                }, onTouchStart: () => {
                    if (isTouchDevice())
                        handleDisplayTooltip(true);
                }, onClick: (e) => {
                    e.preventDefault(); // prevent unexpected blur
                }, children: trigger }), jsx(RadixPopover.Portal, { children: jsxs(RadixPopover.Content, Object.assign({ id: generatedId, ref: contentRef, onMouseEnter: () => handleDisplayTooltip(true), onMouseLeave: () => handleDisplayTooltip(false), side: side, align: align, className: cn(contentStyles({ class: className })), sideOffset: sideOffset !== null && sideOffset !== void 0 ? sideOffset : 0, tabIndex: -1, role: "tooltip" }, props, { children: [jsx("span", { children: children }), jsx(RadixPopover.Arrow, { asChild: true, children: jsx("svg", { width: "12", height: "6", className: arrowStyles(), viewBox: "0 0 10 5", xmlns: "http://www.w3.org/2000/svg", children: jsx("polyline", { points: "0,0 5,5 10,0" }) }) })] })) })] }));
};
Tooltip.displayName = "Tooltip";

export { Tooltip };
